"use client";

import { useEffect, useState } from "react";

/**
 * Background hero video — a small client island so the multi-MB file is only
 * requested where it won't hurt LCP: desktop viewport, motion allowed, a fast
 * connection, and no data-saver. The LCP element is the server-rendered poster
 * <Image> in HomeHero (priority, fills the same box); this component renders
 * nothing until the main thread is idle *and* the video can actually play, so
 * it's purely additive to LCP/INP.
 */
export function HeroVideo({ src }: { src: string }) {
  // `mounted` — we've decided (post-idle) to put the <video> in the tree and
  // let it start buffering. `ready` — the video fired `canplay`, so we fade it
  // over the poster. Until then the poster <Image> underneath is all you see.
  const [mounted, setMounted] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const wideViewport = window.matchMedia("(min-width: 1024px)").matches;

    // navigator.connection is non-standard / absent in some browsers — when it
    // isn't there, only the viewport + motion checks gate.
    const conn = (
      navigator as Navigator & {
        connection?: { effectiveType?: string; saveData?: boolean };
      }
    ).connection;
    const slowOrSaver =
      conn?.saveData === true ||
      (conn?.effectiveType != null && conn.effectiveType !== "4g");

    if (reducedMotion || !wideViewport || slowOrSaver) return;

    // Start loading the video only once the main thread is genuinely idle —
    // i.e. after LCP paint, hydration and any layout work have settled. This
    // adapts to the device instead of a fixed timer: fast machines get the
    // video moments after paint, slow ones wait (protecting INP), and the
    // 3s timeout guarantees it still loads on a permanently-busy page.
    const win = window as Window & {
      requestIdleCallback?: (
        cb: () => void,
        opts?: { timeout: number },
      ) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    if (typeof win.requestIdleCallback === "function") {
      const id = win.requestIdleCallback(() => setMounted(true), {
        timeout: 3000,
      });
      return () => win.cancelIdleCallback?.(id);
    }

    // Safari (no requestIdleCallback) — fall back to a generous timer that
    // still clears the LCP window.
    const id = window.setTimeout(() => setMounted(true), 1200);
    return () => window.clearTimeout(id);
  }, []);

  if (!mounted) return null;

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 z-10 h-full w-full overflow-hidden object-cover object-center transition-opacity duration-700 ${
        ready ? "opacity-100" : "opacity-0"
      }`}
    >
      {/* No `poster` attr on purpose — it would trigger a *second* fetch of the
          poster image; the server-rendered <Image priority> in HomeHero already
          fills this box and covers the gap until `canplay`. */}
      <video
        autoPlay
        loop
        muted
        playsInline
        preload="none"
        onCanPlay={() => setReady(true)}
        className="absolute inset-0 h-full w-full origin-top object-cover object-[50%_30%]"
      >
        <source src={src} type="video/mp4" />
      </video>
    </div>
  );
}
